import { isRecordingTimerRunning } from '@/app-shell/chronicle/hooks/useChronicleSessionControl';
import { chronicleIsPausedAtom, chronicleSessionIdAtom } from '@/atoms/chronicle';
import { pendingTranscribeModeBySessionIdAtom } from '@/atoms/offline-recording/pending-transcribe-mode';
import {
  offlineActiveRecordingIdAtom,
  offlineCapturePausedAtom,
} from '@/atoms/offline-recording/sync';
import {
  hasPendingManualCaptureStop,
  hasPendingManualCaptureStopForSession,
} from '@/lib/desktopExitCaptureStop';
import type { DesktopCaptureActivity } from '@/lib/desktopLogoutConfirmation';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { TranscribeMode } from '@heidi/schema/ml-scribe';
import { findActiveSessionFromCache } from '@heidi/session';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { activeSessionIdAtom } from '@heidi/session/data';
import { getDefaultStore } from 'jotai';

export function resolveModeSessionId({
  liveSessionId,
  chronicleSessionId,
  routeSessionId,
}: {
  liveSessionId: string | null;
  chronicleSessionId: string | null;
  routeSessionId: string | null;
}): string | null {
  // Precedence: live session, Chronicle session, route session, then cached active session.
  return (
    liveSessionId ??
    chronicleSessionId ??
    routeSessionId ??
    findActiveSessionFromCache(reactQueryClient)?.sessionId ??
    null
  );
}

export function detectActiveDesktopCapture(): DesktopCaptureActivity {
  const jotaiStore = getDefaultStore();
  const liveSessionId = jotaiStore.get(activeSessionIdAtom);
  const chronicleSessionId = jotaiStore.get(chronicleSessionIdAtom);
  const chronicleIsActive =
    chronicleSessionId !== null && !jotaiStore.get(chronicleIsPausedAtom);
  const offlineRecordingId = jotaiStore.get(offlineActiveRecordingIdAtom);
  const micStopping =
    liveSessionId !== null
      ? hasPendingManualCaptureStopForSession(liveSessionId)
      : hasPendingManualCaptureStop();
  const chronicleStopping =
    chronicleSessionId !== null &&
    hasPendingManualCaptureStopForSession(chronicleSessionId);
  const routeSessionId = getCurrentSessionId() ?? null;
  const routeSession =
    routeSessionId !== null
      ? reactQueryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId: routeSessionId },
        ])
      : undefined;
  const recordingIsActive =
    (isRecordingTimerRunning() && !micStopping) ||
    (chronicleIsActive && !chronicleStopping);
  const offlineIsActive =
    offlineRecordingId !== null &&
    !jotaiStore.get(offlineCapturePausedAtom) &&
    !micStopping;

  if (!recordingIsActive && !offlineIsActive) return null;

  const modeSessionId = resolveModeSessionId({
    liveSessionId,
    chronicleSessionId,
    routeSessionId,
  });
  let modeSession: QuerySessionResponse | undefined;
  if (modeSessionId !== null) {
    modeSession =
      modeSessionId === routeSessionId
        ? routeSession
        : reactQueryClient.getQueryData<QuerySessionResponse>([
            GET_SESSION_BY_ID,
            { sessionId: modeSessionId },
          ]);
  }
  const pendingTranscribeMode = modeSessionId
    ? jotaiStore.get(pendingTranscribeModeBySessionIdAtom)[modeSessionId]
    : undefined;

  return (pendingTranscribeMode ?? modeSession?.transcribe_mode) ===
    TranscribeMode.DICTATE
    ? 'dictating'
    : 'transcribing';
}
